import { useState, useEffect, useRef } from "react";
import * as S from "./styles";

import { TileLayer, Marker } from "react-leaflet";

import { FilterOption, MapMenu } from "../MapMenu";

import { addOptionList, filterList, isFilterActive } from "./data";

import { MapPopup } from "../../MapPopup";
import { ICoord, useOccCoords } from "services/CordsService";
import { MapList } from "../MapList";
import { MapListItemProps } from "../MapListItem";
import { AddressPopup } from "../../LocalePopup";

import { MapAddButton } from "../MapAddButton";
import { StorePopup } from "../../Popups/StorePopup";
import { OccurrencePopup } from "../../Popups/OccurrencePopup";
import { IAddress } from "interfaces/Address";
import ChangeMarkerColor from "../MapChangeMarkColorButton";

import { markerIcon } from "../MarkerIcon";
import { socket } from "hooks/useSocket";
import { useAuth } from "contexts/authContext";
import { treatSocketLocations } from "utils/treatSocketLocations";
import { useDataList } from "hooks/useDataList";

type Coords = {
  latitude: number;
  longitude: number;
};

export type MarkerType =
  | "policeman"
  | "occVehicle"
  | "occPatrimony"
  | "store"
  | "policemanOnDuty";

type Markers = {
  id: string;
  coords: Coords;
  type: MarkerType;
  name?: string;
  working?: boolean;
};

type SocketMarkers = {
  [id: string]: SocketMarkerBody;
};

export type SocketMarkerBody = {
  coords: Coords;
  token: string;
  working: boolean;
  name?: string;
};

socket.on("connect", () => {
  console.log("[IO] Rastreio de pronta-respostas ativo.");
});

function GlobalMap() {
  const { tenant } = useAuth();
  const [filters, setFilters] = useState<FilterOption>(filterList);
  const CoordsData = useOccCoords().data;
  const [iconsColor, setIconsColor] = useState<string | undefined>();

  const addressesData = useDataList<IAddress>({
    queryKey: 'listAllAddresses',
    route: 'addresses/store',
    filter: {
      notPage: true,
    }
  }).data;

  const position = {
    lat: -15.7303544,
    lng: -47.9325513,
  };

  function getColor(color: string) {
    setIconsColor(color);
  }

  const [policemans, setPolicemans] = useState<Markers[]>([]);
  const [policeman, setPoliceman] = useState<Markers | undefined>();

  const [store, setStore] = useState<ICoord | undefined>();
  const [occurrence, setOccurrence] = useState<ICoord | undefined>();

  const [showStoreForm, setShowStoreForm] = useState<boolean>(false);
  const [showAddOptions, setShowAddOptions] = useState<boolean>(false);

  const mapRef = useRef<L.Map>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [details, setDetails] = useState<MapListItemProps[]>([]);

  useEffect(() => {
    if (tenant?.id) {
      socket.on("onLocationChange", onLocationChange);

      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", onLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (policeman) {
      policemans.forEach((location) => {
        if (policeman.id === location.id) {
          setPoliceman({ ...location });
        }
      });
    }
  }, [policemans]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let detailsList: MapListItemProps[] = [];
    const vehicleActive = isFilterActive(filters, "occVehicle");
    const patrimonyActive = isFilterActive(filters, "occPatrimony");

    if (
      (vehicleActive || patrimonyActive) &&
      CoordsData &&
      CoordsData?.items?.length
    ) {
      CoordsData?.items?.forEach((coords) => {
        const CoordType =
          coords.patrimonialType === 1 ? "occPatrimony" : "occVehicle";
        const CoordsDetail: MapListItemProps = {
          name: CoordType === "occPatrimony" ? "Patrimonial" : "Veicular",
          type: CoordType,
          coords: {
            latitude: coords.coords.x,
            longitude: coords.coords.y,
          },
        };

        if (vehicleActive && CoordType === "occVehicle")
          return detailsList.push(CoordsDetail);
        if (patrimonyActive && CoordType === "occPatrimony")
          return detailsList.push(CoordsDetail);
      });
    }

    const policemanActive = isFilterActive(filters, "policeman");
    const policemanOnDutyActive = isFilterActive(filters, "policemanOnDuty");

    if ((policemanActive || policemanOnDutyActive) && policemans?.length) {
      policemans.forEach((_policeman) => {
        const policemanType = _policeman.working
          ? "policeman"
          : "policemanOnDuty";
        const policemanDetails: MapListItemProps = {
          name: _policeman.name || _policeman.id,
          type: policemanType,
          coords: _policeman.coords,
        };

        if (policemanActive && policemanType === "policeman")
          return detailsList.push(policemanDetails);
        if (policemanOnDutyActive && policemanType === "policemanOnDuty")
          return detailsList.push(policemanDetails);
      });
    }

    const storeActive = isFilterActive(filters, "store");
    if (storeActive && addressesData && addressesData?.items?.length) {
      addressesData.items.forEach((address) => {
        detailsList.push({
          name: address.name || "Estabelecimento",
          coords: {
            latitude: address.CoordX || 0,
            longitude: address.CoordY || 0,
          },
          type: "store",
        });
      });
    }

    setDetails([...detailsList]);
  }, [CoordsData, policemans, filters, addressesData]);

  function onLocationChange(locations: SocketMarkers) {
    const _locations = treatSocketLocations({
      socketLocations: locations,
      tenantId: tenant?.id ?? "",
    });

    setPolicemans([..._locations]);
    setIsLoading(false);
  }

  function onFilterChange(filtersChanged: FilterOption) {
    setFilters({ ...filtersChanged });
  }

  function changeMapLocation(coords: Coords) {
    mapRef.current?.setView(
      { lat: coords.latitude, lng: coords.longitude },
      13
    );
  }

  function openAddForm(id: string) {
    resetPopups();

    switch (id) {
      case "store":
        setShowStoreForm(true);
        break;
      default:
        break;
    }
  }

  function resetPopups() {
    setPoliceman(undefined);
    setOccurrence(undefined);
    setStore(undefined);
    setShowStoreForm(false);
  }

  function renderPolicemanMarker(coordinate: Markers) {
    return (
      <Marker
        icon={markerIcon({
          color: iconsColor,
          type: "policeman",
        })}
        position={{
          lat: coordinate.coords.latitude,
          lng: coordinate.coords.longitude,
        }}
        eventHandlers={{
          click: () => {
            resetPopups();
            setPoliceman({ ...coordinate });
          },
        }}
      />
    );
  }

  if (isLoading) return <></>;

  return (
    <S.Wrapper>
      <MapList list={details} onPinClick={changeMapLocation} />

      <S.Map center={position} zoom={4} ref={mapRef} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {(filters.policeman || filters.policemanOnDuty) &&
          policemans?.map((coordinate) => {
            if (filters.policeman.active && coordinate.working) {
              return renderPolicemanMarker(coordinate);
            }

            if (filters.policemanOnDuty.active && !coordinate.working) {
              return renderPolicemanMarker(coordinate);
            }
          })}

        {filters.occVehicle &&
          filters.occVehicle.active &&
          CoordsData?.items?.map((Coord) => {
            if (Coord.patrimonialType === 2) {
              return (
                <Marker
                  icon={markerIcon({
                    color: iconsColor,
                    type: "occVehicle",
                  })}
                  position={{
                    lat: Coord.coords.x,
                    lng: Coord.coords.y,
                  }}
                  eventHandlers={{
                    click: () => {
                      resetPopups();
                      setOccurrence({ ...Coord });
                    },
                  }}
                />
              );
            }
          })}

        {filters.occPatrimony &&
          filters.occPatrimony.active &&
          CoordsData?.items?.map((coord) => {
            if (coord.patrimonialType === 1) {
              return (
                <Marker
                  icon={markerIcon({
                    color: iconsColor,
                    type: "occPatrimony",
                  })}
                  position={{
                    lat: coord.coords.x,
                    lng: coord.coords.y,
                  }}
                  eventHandlers={{
                    click: () => {
                      resetPopups();
                      setOccurrence({ ...coord });
                    },
                  }}
                />
              );
            }
          })}

        {filters.store &&
          filters.store.active &&
          addressesData?.items?.map(
            (address) =>
              address.CoordX &&
              address.CoordY && (
                <Marker
                  icon={markerIcon({
                    color: iconsColor,
                    type: "store",
                  })}
                  position={{
                    lat: address.CoordX,
                    lng: address.CoordY,
                  }}
                  eventHandlers={{
                    click: () => {
                      resetPopups();
                      setStore({
                        id: address.id || "",
                        coords: {
                          x: address.CoordX || 0,
                          y: address.CoordY || 0,
                        },
                        searchDistance: 0,
                      });
                    },
                  }}
                />
              )
          )}

        <MapMenu onFilterChange={onFilterChange} filters={filters} />

        {policeman && (
          <MapPopup
            policemanCoords={policeman}
            onClose={() => setPoliceman(undefined)}
          />
        )}

        {showStoreForm && (
          <AddressPopup
            onClose={() => {
              setShowStoreForm(false);
            }}
          />
        )}

        <MapAddButton
          options={addOptionList}
          showOptions={showAddOptions}
          onClick={() => setShowAddOptions(!showAddOptions)}
          onClose={() => setShowAddOptions(false)}
          onOptionClick={openAddForm}
        />
        <ChangeMarkerColor getColor={getColor} />

        {store && (
          <StorePopup coords={store} onClose={() => setStore(undefined)} />
        )}

        {occurrence && (
          <OccurrencePopup
            coords={occurrence}
            onClose={() => setOccurrence(undefined)}
            io={socket}
          />
        )}
      </S.Map>
    </S.Wrapper>
  );
}

export { GlobalMap };
